import { KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconButton, PrimaryButton, SecondaryButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { colors, shadowM, radius } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { CellEditorProps } from './types';
import { useEffectToHideEditorOnHorizontalScrollForPopupEditors } from './useEffectToHideEditorOnHorizontalScrollForPopupEditors';

const EDITOR_WIDTH_PX = 520;
export const EDITOR_MIN_HEIGHT = '200px';

/**
 * AG Grid starts an edit session when a printable key is pressed on the focused
 * cell, handing the editor that key rather than typing it. On an empty cell,
 * seeding the editor with it keeps the keystroke. Anything longer (Enter, F2,
 * Backspace) opened the editor rather than started a word.
 *
 * Only ever seeds an empty value: on one that already has text, a mistyped
 * character must not replace it.
 */
const getInitialValue = (
  value: string | null | undefined,
  eventKey: string | null
): string => value || (eventKey?.length === 1 ? eventKey : '');

const Card = styled(FlexBox)`
  width: ${EDITOR_WIDTH_PX}px;
  background-color: ${colors.surfaceDefault};
  border-radius: ${radius.medium};
  box-shadow: ${shadowM};
`;

type RichTextEditorSlotProps = {
  value: string;
  /** A user edit. Handed to AG Grid so it survives ending the session by click-away. */
  onValueChange: (markdown: string) => void;
  /**
   * Shows text the user did not type without handing it to AG Grid, so it is
   * persisted only if they go on to edit it or press Save.
   */
  setValue: (markdown: string) => void;
  /** Holds Save while something is still writing into the editor. */
  setIsSaveBlocked: (isSaveBlocked: boolean) => void;
};

type RichTextCellEditorProps = {
  props: CellEditorProps<string>;
  title: string;
  closeLabel: string;
  children: (slotProps: RichTextEditorSlotProps) => ReactNode;
};

/**
 * The popup chrome and AG Grid value plumbing shared by every cell editor that
 * edits markdown. Which editor fills it is the caller's choice.
 */
export const RichTextCellEditor = ({
  props,
  title,
  closeLabel,
  children,
}: RichTextCellEditorProps) => {
  useEffectToHideEditorOnHorizontalScrollForPopupEditors(props.api);

  const [isSaveBlocked, setIsSaveBlocked] = useState(false);

  /**
   * The editor's own value. Only what the user has touched is handed to AG Grid
   * via `onValueChange`, because whatever sits in the grid's pending edit value
   * is committed when the edit session ends any way other than a cancel —
   * clicking another cell, for one. An AI description the user never asked for
   * and never looked at must not be PATCHed onto the component that way, so it
   * lives here until they edit it or press Save.
   */
  const [value, setValue] = useState(() =>
    getInitialValue(props.value, props.eventKey)
  );

  const onValueChange = (markdown: string) => {
    setValue(markdown);
    props.onValueChange(markdown);
  };

  // A seeded keystroke is the user typing, so it goes to the grid like any
  // other edit.
  useEffect(() => {
    if (value !== (props.value ?? '')) {
      props.onValueChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only sync
  }, []);

  const { i18nLocale } = props.context;

  const cancel = () => props.api.stopEditing(true);
  const save = () => {
    props.onValueChange(value);
    props.api.stopEditing();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      cancel();
    }
  };

  return (
    <div onKeyDown={onKeyDown}>
      <Card
        role="dialog"
        aria-label={title}
        flexDirection="column"
        gap="medium"
        padding="medium"
      >
        <FlexBox justify="space-between" align="center">
          <Text variant="text1Bold">{title}</Text>
          <IconButton
            iconName={IconName.CLOSE}
            color={colors.icon}
            label={closeLabel}
            onClick={cancel}
          />
        </FlexBox>
        {children({ value, onValueChange, setValue, setIsSaveBlocked })}
        <FlexBox gap="small" justify="flex-end">
          <SecondaryButton onClick={cancel}>
            {t(i18nLocale, 'inventory:richTextCellEditor.cancel', 'Cancel')}
          </SecondaryButton>
          <PrimaryButton
            onClick={save}
            isDisabled={isSaveBlocked}
            i18nLocale={i18nLocale}
          >
            {t(i18nLocale, 'inventory:richTextCellEditor.save', 'Save')}
          </PrimaryButton>
        </FlexBox>
      </Card>
    </div>
  );
};
